import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

/**
 * Paquete de números de control fiscales (Unidigital, ADM-732). No reutilizar
 * `SmsPackage`: la cantidad viaja en `quantity`, no en `amountmsm`.
 */
export interface ControlNumberPackage {
    _id: string;
    title: string;
    /** `null` cuando el paquete no tiene descripción: no pintarlo. */
    description: string | null;
    /** Cantidad de números de control; es la clave del paquete en api-admin. */
    quantity: number;
    /** Precio USD NETO, sin IVA. */
    amount: number;
}

export interface ControlNumberPackagesResponse {
    title: string;
    message: string;
    data: ControlNumberPackage[];
}

/** Primera condición que falla, en este orden. `null` = se puede solicitar. */
export type FreeRequestReason = 'no_integration' | 'no_fiscal_plan' | 'not_free' | 'above_threshold';

/**
 * Estado de la solicitud gratuita de números de control (ADM-827). Lo calcula api-admin y api
 * lo reenvía tal cual: el front no recalcula la elegibilidad ni hardcodea el umbral.
 */
export interface FreeRequestStatus {
    /** Integración UNIDIGITAL. Sin ella, `limit`, `purchased` y `available` valen 0. */
    hasIntegration: boolean;
    fiscalPlan: boolean;
    /** `limit + purchased`. */
    available: number;
    limit: number;
    purchased: number;
    /** Disponible máximo con el que se habilita la solicitud. */
    threshold: number;
    /** Existe el paquete `freeRequest` y vale $0. */
    freeMode: boolean;
    /** `=== (reason === null)`. */
    canRequest: boolean;
    reason: FreeRequestReason | null;
    /** `null` si no hay paquete `freeRequest`. */
    package: { quantity: number; amount: number } | null;
}

export interface FreeRequestStatusResponse {
    title: string;
    message: string;
    data: FreeRequestStatus;
}

export interface FreeRequestCreatedResponse {
    title: string;
    message: string;
    /** `available` es el disponible DESPUÉS de acreditar. */
    data: { billPlanId: string; quantity: number; available: number };
}

@Injectable({
    providedIn: 'root',
})
export class ControlNumbersService {
    private readonly API_URL = environment.API_URL;
    private packages$?: Observable<ControlNumberPackage[]>;

    constructor(private http: HttpClient) {}

    /** Catálogo ordenado por `quantity` ascendente (lo ordena `api`). Se cachea; un error reinicia el caché. */
    getControlNumberPackages$(): Observable<ControlNumberPackage[]> {
        if (!this.packages$) {
            this.packages$ = this.http.get<ControlNumberPackagesResponse>(`${this.API_URL}/control-numbers/packages`).pipe(
                map(res => res?.data ?? []),
                catchError(err => {
                    this.packages$ = undefined;
                    return throwError(() => err);
                }),
                shareReplay({ bufferSize: 1, refCount: false }),
            );
        }
        return this.packages$;
    }

    /** Estado de la solicitud gratuita (ADM-827). Sin caché: cambia con cada crédito y cada emisión. */
    getFreeRequestStatus$(): Observable<FreeRequestStatus | null> {
        return this.http.get<FreeRequestStatusResponse>(`${this.API_URL}/control-numbers/free-request`).pipe(map(res => res?.data ?? null));
    }

    /** Acredita el paquete gratuito. Cuerpo vacío: api toma el tenant y el actor del token. */
    requestFreeControlNumbers$(): Observable<FreeRequestCreatedResponse> {
        return this.http.post<FreeRequestCreatedResponse>(`${this.API_URL}/control-numbers/free-request`, {});
    }
}
